import React, {
  Children,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useContextSelector } from 'use-context-selector';
import { BucketLogData } from '@bucketplace/bucket-log';

import Portal from '../../../components/Portal';
import { EditorContext } from '../../../contexts';
import { useEditorNode } from '../../../hooks';
import { DocumentNode, DocumentPath } from '../../../types';
import { useTabContext } from '../Tab';

export interface TabItemProps {
  index: number;
  path: DocumentPath;
  logData?: BucketLogData;
}

export function TabItem({ children, index, path, logData }: PropsWithChildren<TabItemProps>) {
  const { id, currentIndex } = useTabContext();
  const { visibleButton } = useEditorInitializer({ index, path });

  function renderTabButtons() {
    const [tabOnButton, tabOffButton] = filterChildren(children, 'tabButton');
    if (!tabOnButton || !tabOffButton) { return; }
    const isCurrentTab = index === currentIndex;

    if (visibleButton) {
      return (
        <>
          { visibleButton === 'on' && React.cloneElement(tabOnButton, { index, logData }) }
          { visibleButton === 'off' &&
            React.cloneElement(tabOffButton, { index, logData }) }
        </>
      );
    }

    return (
      <>
        { isCurrentTab && React.cloneElement(tabOnButton, { index, logData }) }
        { !isCurrentTab && React.cloneElement(tabOffButton, { index, logData }) }
      </>
    );
  }

  function renderTabContent() {
    const [tabContent] = filterChildren(children, 'tabContent');
    if (!tabContent) { return null; }

    return (
      <Portal containerSelector={`#tab-${id}-panel`}>
        { React.cloneElement(tabContent, { index }) }
      </Portal>
    );
  }

  return (
    <>
      { renderTabButtons() }
      { renderTabContent() }
    </>
  );
}

function useEditorInitializer({
  index,
  path,
}: {
  index: number,
  path: DocumentPath,
}) {
  const context = useContextSelector(EditorContext, (context) => context);
  const isInEditor = !!context;
  const { selectIndex } = useTabContext();

  const [visibleButton, setVisibleButton] = useState<
  undefined | 'on' | 'off'
  >();
  const tabOnButtonNode = useEditorNode([...path, 0]);
  const tabOffButtonNode = useEditorNode([...path, 1]);

  function configureVisibleButton() {
    if (!isInEditor) { return; }
    if (tabOnButtonNode.isChildSelected) {
      setVisibleButton('on');
      selectIndex(index);
    } else if (tabOffButtonNode.isChildSelected) {
      setVisibleButton('off');
    } else {
      setVisibleButton(undefined);
    }
  }
  useEffect(configureVisibleButton, [
    tabOnButtonNode.isChildSelected,
    tabOffButtonNode.isChildSelected,
  ]);

  return { visibleButton };
}

function filterChildren(children: ReactNode, id: string): ReactElement[] {
  return Children.toArray(children).reduce<ReactElement[]>((acc, child) => {
    if (!React.isValidElement(child)) { return acc; }
    const childId = (
      child.type as React.JSXElementConstructor<any> & { id: string }
    ).id;
    return childId === id ? [...acc, child] : acc;
  }, []);
}
